import type { TFunction } from 'i18next';

/**
 * The lines the SYSTEM writes into a dispatch note: the reorder reason the
 * prefill opens with, and the task lines a Stock-tab hand-off or a join
 * appends. Every one of them is about the order it was written for.
 */
const GENERATED: Array<{ key: string; placeholders: string[] }> = [
  { key: 'logistics.orderDialog.reorderNote', placeholders: ['material', 'days', 'lead'] },
  { key: 'logistics.orderDialog.fromTaskNote', placeholders: ['task', 'start', 'end', 'storey'] },
  { key: 'logistics.orderDialog.fromTaskNoteNoCad', placeholders: ['task', 'start', 'end'] },
  { key: 'logistics.orderDialog.joinTaskNote', placeholders: ['task', 'start', 'end', 'qty', 'unit', 'storey'] },
  { key: 'logistics.orderDialog.joinTaskNoteNoCad', placeholders: ['task', 'start', 'end', 'qty', 'unit'] },
];

/** Every catalogue the app ships: a note written under one viewer's language
 *  has to strip under another's. */
const LANGUAGES = ['en', 'ja'];

/** The stand-in each placeholder is filled with; it becomes a wildcard in the
 *  pattern. Only the template's OWN placeholders are passed, so a translator
 *  that echoes its options (a test double) sees exactly what the writer sent. */
const PLACEHOLDER = '__V__';

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Each template as a whole-line pattern, with its placeholders wild. The
 *  optional quotes let a test's JSON-stringifying translator double match its
 *  own numbers. */
function generatedPatterns(t: TFunction): RegExp[] {
  return LANGUAGES.flatMap((lng) =>
    GENERATED.map(({ key, placeholders }) => {
      const fillers = Object.fromEntries(placeholders.map((name) => [name, PLACEHOLDER]));
      const filled = escapeRegExp(t(key, { ...fillers, lng }));
      return new RegExp(`^${filled.replace(/"?__V__"?/g, '.+?')}$`);
    }),
  );
}

/**
 * What a previous order's dispatch note still says once the lines the system
 * wrote for THAT order are taken out: the standing instructions a person typed
 * — gate access, crane booking, who to call — which stay true order to order.
 *
 * Carrying the whole note forward fed the prefill its own output: an order
 * raised from "Order now" opens with a reorder line, the next order carried
 * that line as if it were an instruction, and every generation added one
 * more. '' when nothing hand-written is left.
 */
export function carriedNote(note: string, t: TFunction): string {
  if (note.trim() === '') return '';
  const generated = generatedPatterns(t);
  const kept = note.split('\n').filter((line) => !generated.some((re) => re.test(line.trim())));
  return kept.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}
